// P:\0PJ\synforge\frontend\app\settings\billing\page.tsx v4 2026.08.26 - card number/expiry/cvc raw inputs replaced with Stripe Elements CardElement (PCI: card data no longer touches our server)
'use client';

import { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { billingAPI } from '@/lib/api';
import { useLocale } from '@/contexts/LocaleContext';

const COUNTRIES = ['Australia', 'United States', 'United Kingdom', 'Canada', 'South Korea', 'Japan', 'Germany', 'France'];
const TAX_ID_TYPES = ['Select tax ID type', 'ABN (Australia)', 'EIN (United States)', 'VAT (EU/UK)', 'GST (Canada)'];

function Card({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-5 pb-8 mb-8 border-b border-white/[0.05] last:border-0 last:mb-0 last:pb-0">
      <div>
        <h3 className="text-base font-semibold text-white tracking-tight">{title}</h3>
        {desc && <p className="text-xs text-white/30 mt-1">{desc}</p>}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-white/50 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls = "w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white text-sm placeholder:text-white/20 focus:outline-none focus:border-purple-500/60 transition-colors";

// Stripe CardElement 는 iframe 이라 우리 inputCls 를 못 쓴다 - 감싸는 div 에만 같은 톤을 입힌다.
const cardElementWrapperCls = "w-full bg-[#13131f] border border-white/[0.08] rounded-xl px-4 py-3 focus-within:border-purple-500/60 transition-colors";
const cardElementOptions = {
  // 우편번호는 위쪽 Address 칸에서 이미 따로 받으므로 중복 수집하지 않는다.
  //   (내장 우편번호 필드는 카드 BIN 국가 기준으로 자릿수를 검증해 실제 청구지와 안 맞을 수 있다.)
  hidePostalCode: true,
  style: {
    base: {
      color: '#ffffff',
      fontSize: '14px',
      '::placeholder': { color: 'rgba(255,255,255,0.2)' },
    },
    invalid: { color: '#f87171' },
  },
};

// ── AddCardForm — <Elements> 내부에서만 렌더된다(useStripe/useElements 훅 제약). ──
// 카드 전체번호/CVC 는 이 컴포넌트 밖으로 절대 나가지 않는다(Stripe.js 가 직접 Stripe 서버로 전송).
function AddCardForm({ onAdded, onCancel }: { onAdded: () => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();

  const [fullName, setFullName] = useState('');
  const [country, setCountry]   = useState('Australia');
  const [address, setAddress]   = useState('');
  const [useDiffBillingName, setUseDiffBillingName] = useState(false);
  const [billingName, setBillingName] = useState('');
  const [taxIdType, setTaxIdType] = useState(TAX_ID_TYPES[0]);
  const [cardError, setCardError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setCardError('');
    if (!stripe || !elements) { setCardError('Payment form is still loading. Try again in a moment.'); return; }
    const cardEl = elements.getElement(CardElement);
    if (!cardEl) { setCardError('Card input not ready.'); return; }

    setSubmitting(true);
    try {
      // 1단계 — 우리 백엔드에서 SetupIntent 발급(Stripe Customer 도 여기서 생성/재사용됨).
      const { data: setup } = await billingAPI.createSetupIntent();

      // 2단계 — 카드 데이터는 Stripe.js 가 iframe 안에서 직접 Stripe 서버로 보낸다.
      //   우리 서버는 이 요청을 절대 보지 못한다(PCI 핵심).
      const result = await stripe.confirmCardSetup(setup.client_secret, {
        payment_method: {
          card: cardEl,
          billing_details: {
            name: useDiffBillingName ? billingName : fullName,
            address: { country: undefined, line1: address },
          },
        },
      });

      if (result.error) {
        setCardError(result.error.message || 'Card verification failed.');
        return;
      }
      const pmId = result.setupIntent?.payment_method as string;
      if (!pmId) { setCardError('Card verification did not return a payment method.'); return; }

      // 3단계 — 우리 DB(PaymentMethod)에 참조(id)만 저장. 전체번호/CVC 는 여기 없다.
      await billingAPI.confirmPaymentMethod(pmId, setup.customer_id);

      cardEl.clear();
      onAdded();
    } catch (err: any) {
      setCardError(err.response?.data?.message || 'Failed to add card.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 space-y-4">
      <Field label="Full Name">
        <input className={inputCls} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="HUGH" />
      </Field>
      <Field label="Country or Region">
        <select className={inputCls} value={country} onChange={(e) => setCountry(e.target.value)}>
          {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </Field>
      <Field label="Address">
        <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street address" />
      </Field>
      <Field label="Card Details">
        <div className={cardElementWrapperCls}>
          <CardElement options={cardElementOptions} />
        </div>
      </Field>
      <label className="flex items-center gap-2 text-xs text-white/50 cursor-pointer">
        <input type="checkbox" checked={useDiffBillingName} onChange={(e) => setUseDiffBillingName(e.target.checked)} className="accent-purple-600" />
        Use a different name on invoice
      </label>
      {useDiffBillingName && (
        <Field label="Name on Invoice">
          <input className={inputCls} value={billingName} onChange={(e) => setBillingName(e.target.value)} placeholder="Billing name" />
        </Field>
      )}
      <Field label="Business Tax ID (optional)">
        <select className={inputCls} value={taxIdType} onChange={(e) => setTaxIdType(e.target.value)}>
          {TAX_ID_TYPES.map((tt) => <option key={tt} value={tt}>{tt}</option>)}
        </select>
        <p className="text-[11px] text-white/25 mt-1.5">If you provide a tax ID, the "Full Name" above must be your business name.</p>
      </Field>
      {cardError && <p className="text-[11px] text-red-400">{cardError}</p>}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSubmit}
          disabled={submitting || !stripe}
          className="bg-purple-600 hover:bg-purple-500 disabled:opacity-40 text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
        >
          {submitting ? 'Adding...' : 'Update'}
        </button>
        <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs px-4 py-2 transition-colors">Cancel</button>
      </div>
    </div>
  );
}

export default function BillingPage() {
  const { t } = useLocale();

  const [cards, setCards] = useState<any[]>([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  const loadCards = () => {
    billingAPI.getPaymentMethods().then((res) => setCards(res.data)).catch(() => {});
  };

  const [recharges, setRecharges] = useState<any[]>([]);

  // Billing History — 카드 충전(recharge) 거래만 뽑아 보여준다. myTokens() 가 이미
  //   reason="recharge" 건을 memo(payment_intent id, usd 금액)와 함께 준다(billing/tokens
  //   화면과 같은 원본 - 여기서는 결제 영수증 관점으로 다시 보여주는 것뿐, 별도 API 불필요).
  const loadRecharges = () => {
    billingAPI.myTokens()
      .then((res) => {
        const list = (res.data.transactions || []).filter((t: any) => t.reason === "recharge");
        setRecharges(list);
      })
      .catch(() => {});
  };

  useEffect(() => { loadCards(); loadRecharges(); }, []);

  const parseUsd = (memo: string): string => {
    const m = memo.match(/usd=\$?([\d.]+)/);
    return m ? m[1] : "";
  };

  // Publishable key 는 백엔드(get_stripe_config)에서 조회 - 공개용 키라 노출돼도 안전하다.
  useEffect(() => {
    billingAPI.getStripeConfig()
      .then((res) => setStripePromise(loadStripe(res.data.publishable_key)))
      .catch(() => {});
  }, []);

  const handleDeleteCard = async (id: string) => {
    try {
      await billingAPI.deletePaymentMethod(id);
      loadCards();
    } catch (err) {
      console.error('Failed to delete card:', err);
    }
  };

  const handleSetDefault = async (id: string) => {
    setSettingDefaultId(id);
    try {
      await billingAPI.setDefaultPaymentMethod(id);
      loadCards();
    } catch (err) {
      console.error('Failed to set default card:', err);
    } finally {
      setSettingDefaultId(null);
    }
  };

  return (
    <div className="p-6 max-w-[640px] space-y-4">
      <Card title="Payment Method" desc="Up to 3 cards. Used to purchase Synforge tokens.">
        {cards.map((c) => (
          <div key={c.id} className="flex items-center justify-between bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 bg-white/10 rounded flex items-center justify-center text-xs text-white/40 font-mono uppercase">{c.brand}</div>
              <div>
                <p className="text-sm text-white">.... .... .... {c.last4}{c.is_default && <span className="ml-2 text-[10px] text-purple-400">Default</span>}</p>
                <p className="text-xs text-white/30">Expires {String(c.exp_month).padStart(2,"0")}/{String(c.exp_year).slice(-2)}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {!c.is_default && (
                <button
                  onClick={() => handleSetDefault(c.id)}
                  disabled={settingDefaultId === c.id}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors disabled:opacity-40"
                >
                  {settingDefaultId === c.id ? 'Setting...' : 'Set as default'}
                </button>
              )}
              <button onClick={() => handleDeleteCard(c.id)} className="text-xs text-white/30 hover:text-red-400 transition-colors">Remove</button>
            </div>
          </div>
        ))}

        {cards.length === 0 && !showAddCard && (
          <p className="text-xs text-white/30">No payment methods added yet.</p>
        )}

        {showAddCard ? (
          stripePromise ? (
            <Elements stripe={stripePromise}>
              <AddCardForm
                onAdded={() => { setShowAddCard(false); loadCards(); }}
                onCancel={() => setShowAddCard(false)}
              />
            </Elements>
          ) : (
            <p className="text-xs text-white/30">Loading payment form...</p>
          )
        ) : (
          cards.length < 3 && (
            <button onClick={() => setShowAddCard(true)} className="text-xs text-purple-400 hover:text-purple-300 transition-colors">+ Add payment method</button>
          )
        )}
      </Card>

      <Card title="Billing History">
        {recharges.length === 0 ? (
          <p className="text-xs text-white/30">No billing history yet. Real invoices will appear here once payment processing is connected.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-white/30 border-b border-white/[0.06]">
                <th className="text-left font-normal py-2">Date</th>
                <th className="text-left font-normal py-2">Details</th>
                <th className="text-right font-normal py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {recharges.map((r) => {
                const usd = parseUsd(r.memo || "");
                return (
                  <tr key={r.id} className="border-b border-white/[0.04] last:border-0">
                    <td className="py-2.5 text-white/50">{new Date(r.created_at).toLocaleString()}</td>
                    <td className="py-2.5 text-white">{usd ? `$${usd}` : "Recharge"} — {Number(r.amount).toLocaleString()} tokens</td>
                    <td className="py-2.5 text-right text-emerald-400 uppercase tracking-wide">Paid</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}